/**
 * Follow-up Emails Service
 *
 * Data access layer for follow_up_emails table.
 * Handles follow-up email template operations.
 */

import { supabase, handleSupabaseError } from '../lib/supabase';
import type { Database } from '../lib/database.types';
import { applyAccessControl, type AccessControl } from '../lib/accessControl';

export type FollowUpEmail = Database['public']['Tables']['follow_up_emails']['Row'];
export type FollowUpEmailInsert = Database['public']['Tables']['follow_up_emails']['Insert'];
export type FollowUpEmailUpdate = Database['public']['Tables']['follow_up_emails']['Update'];

/**
 * Extended type with location info
 */
export interface FollowUpEmailWithLocation extends FollowUpEmail {
  location_name?: string;
}

/**
 * Get all follow-up emails with access control
 */
export async function getFollowUpEmails(
  accessControl: AccessControl | null,
  filters?: {
    locationId?: string;
    language?: string;
  }
) {
  try {
    let query = supabase
      .from('follow_up_emails')
      .select(`
        *,
        locations!inner(location_name, merchant_id)
      `)
      .order('created_at', { ascending: false });

    // Apply access control filtering
    query = applyAccessControl(query, accessControl, {
      merchantIdColumn: 'locations.merchant_id',
      locationIdColumn: 'location_id'
    });

    if (filters?.locationId) {
      query = query.eq('location_id', filters.locationId);
    }

    if (filters?.language) {
      query = query.eq('language', filters.language);
    }

    const { data, error } = await query;

    if (error) throw error;

    // Format the response
    const formattedData = (data || []).map((item: any) => ({
      ...item,
      location_name: item.locations?.location_name,
    }));

    return { data: formattedData as FollowUpEmailWithLocation[], error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get a follow-up email by location and language
 */
export async function getFollowUpEmailByLocationAndLanguage(
  locationId: string,
  language: string = 'en'
) {
  try {
    const { data, error } = await supabase
      .from('follow_up_emails')
      .select('*')
      .eq('location_id', locationId)
      .eq('language', language)
      .maybeSingle();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get a follow-up email by ID
 */
export async function getFollowUpEmailById(id: string) {
  try {
    const { data, error } = await supabase
      .from('follow_up_emails')
      .select(`
        *,
        locations(location_name, merchant_id)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Create a new follow-up email
 */
export async function createFollowUpEmail(data: FollowUpEmailInsert) {
  try {
    const { data: newEmail, error } = await supabase
      .from('follow_up_emails')
      .insert(data)
      .select()
      .single();

    if (error) throw error;

    return { data: newEmail, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Update a follow-up email
 */
export async function updateFollowUpEmail(id: string, data: FollowUpEmailUpdate) {
  try {
    const { data: updatedEmail, error } = await supabase
      .from('follow_up_emails')
      .update({
        ...data,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return { data: updatedEmail, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get distinct languages that have existing follow-up emails for a location
 */
export async function getExistingFollowUpEmailLanguages(locationId: string) {
  try {
    const { data, error } = await supabase
      .from('follow_up_emails')
      .select('language')
      .eq('location_id', locationId);

    if (error) throw error;

    const languages = [...new Set((data || []).map(row => row.language).filter(Boolean))] as string[];
    return { data: languages, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Delete a follow-up email
 */
export async function deleteFollowUpEmail(id: string) {
  try {
    const { error } = await supabase
      .from('follow_up_emails')
      .delete()
      .eq('id', id);

    if (error) throw error;

    return { error: null };
  } catch (error) {
    return { error: handleSupabaseError(error) };
  }
}

/**
 * Upsert a follow-up email (create or update based on location_id + language)
 */
export async function upsertFollowUpEmail(data: FollowUpEmailInsert) {
  try {
    const { data: upsertedEmail, error } = await supabase
      .from('follow_up_emails')
      .upsert(data, {
        onConflict: 'location_id,language'
      })
      .select()
      .single();

    if (error) throw error;

    return { data: upsertedEmail, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}
